/*
 * Carbisdale - LinkPicker
 *
 * Kapselt den Section-Link-Picker, der erscheint wenn der User am
 * Wortanfang "@" tippt. Zeigt eine gefilterte Liste der Separatoren,
 * navigiert per Pfeiltasten, fuegt beim Enter einen Link-Marker ein.
 *
 * Rolle wurde aus EditorInput.js extrahiert, um die Facade dort schlank
 * zu halten. EditorInput haelt eine Instanz und delegiert an sie.
 */

import { Position } from '../EditorModel.js'
import { EDITOR } from '../../config.js'

const MAX_VISIBLE = EDITOR.LINK_PICKER_MAX_VISIBLE

export class LinkPicker {
    /**
     * @param {import('../EditorModel.js').EditorModel} model
     * @param {import('../EditorView.js').EditorView} view
     * @param {object} [input] - EditorInput, fuer Smart-Blanks + atomares Undo
     *   beim Link-Einfuegen. Optional (Fallback ohne Smart Blanks, falls null).
     */
    constructor(model, view, input = null) {
        this.model = model
        this.view = view
        this.input = input

        // Picker-State
        this._active = false
        this._filter = ''
        this._items = []
        this._index = 0
        // Index des obersten sichtbaren Items. Single Source of Truth fuer den
        // Scroll-Zustand: Keyboard-Nav haelt den selektierten Index sichtbar,
        // das Mausrad verschiebt ihn frei.
        this._scrollOffset = 0
        // Fraktionaler Mausrad-Akkumulator: handleWheel bekommt gedrosselte
        // Teil-Zeilen (<1) und steppt erst, wenn eine volle Zeile zusammenkommt.
        this._wheelAccumulator = 0
        this._sepMap = null
        // Bounds werden nach Overlay-Render von aussen gesetzt (main.js)
        // und fuer Klick-Hit-Tests gebraucht.
        this._bounds = null
        // Anchor-Position (x, y in Char-Koordinaten), gesetzt beim ersten
        // Render. Wiederverwendet, damit der Picker nicht bei jedem Re-Render
        // (z.B. durch Cursor-Blink) seine Position neu aus cursorPos berechnet
        // — was sonst durch Pixel-Rundung zu sichtbarem Springen fuehrt und
        // Hover/Klick-Bounds asynchron zur Maus laufen laesst.
        this._anchor = null
    }

    /** @returns {boolean} Ob der Picker gerade offen ist. */
    get isActive() { return this._active }

    /** Liest die Picker-Bounds (fuer Hit-Tests). */
    get bounds() { return this._bounds }

    /** Setzt die Picker-Bounds nach dem Overlay-Render. */
    setBounds(bounds) { this._bounds = bounds }

    /** Liest die Anchor-Position (oder null, wenn noch kein Render lief). */
    get anchor() { return this._anchor }

    /** Setzt die Anchor-Position beim ersten Render einer Open-Session. */
    setAnchor(x, y) { this._anchor = { x, y } }

    /**
     * Setzt transienten State zurueck (Projekt-Wechsel).
     */
    reset() {
        this._active = false
        this._filter = ''
        this._items = []
        this._index = 0
        this._scrollOffset = 0
        this._wheelAccumulator = 0
        this._sepMap = null
        this._bounds = null
        this._anchor = null
    }

    /**
     * Oeffnet den Picker. Schlaegt fehl (Picker bleibt zu) wenn keine
     * Separatoren existieren.
     *
     * @param {Object} [options]
     * @param {boolean} [options.triggerCharPresent=true]
     *   true (Standard): Der Picker wurde durch ein getipptes '@' geoeffnet
     *     — `_accept()` entfernt dieses '@' beim Bestaetigen.
     *   false: Der Picker wurde programmatisch geoeffnet (z.B. ueber das
     *     Context Menu '@Link') — kein '@' im Text, `_accept()` entfernt
     *     0 Zeichen.
     */
    open(options = {}) {
        const sepMap = this.model.buildSeparatorMap()
        if (sepMap.size === 0) return

        this._active = true
        this._triggerCharPresent = options.triggerCharPresent !== false
        this._filter = ''
        this._index = 0
        this._scrollOffset = 0
        this._wheelAccumulator = 0
        this._sepMap = sepMap
        this._anchor = null  // Erste Render-Phase berechnet den Anchor neu
        this._updateItems()
    }

    /**
     * Schliesst den Picker ohne Textaenderung.
     */
    close() {
        this._active = false
        this._filter = ''
        this._items = []
        this._index = 0
        this._scrollOffset = 0
        this._wheelAccumulator = 0
        this._sepMap = null
        this._bounds = null
        // Full Render erzwingen, damit Picker-Overlay komplett verschwindet
        this.view.markDirty()
    }

    /** Bricht den Picker ab (Alias fuer close — Text bleibt stehen). */
    cancel() { this.close() }

    /**
     * Behandelt Tasten im Picker-Modus.
     * @param {KeyboardEvent} e
     * @returns {boolean} Immer true — der Picker verschluckt alle Tasten.
     */
    handleKey(e) {
        switch (e.key) {
            case 'ArrowDown':
                if (this._items.length > 0) {
                    // Clampen statt zyklisch — am Ende anschlagen ist
                    // uebersichtlicher als zum ersten Item zurueckzuspringen.
                    this._index = Math.min(this._index + 1, this._items.length - 1)
                    this._ensureIndexVisible()
                }
                return true

            case 'ArrowUp':
                if (this._items.length > 0) {
                    this._index = Math.max(this._index - 1, 0)
                    this._ensureIndexVisible()
                }
                return true

            case 'Enter': {
                if (this._items.length > 0) {
                    this._accept(this._items[this._index].id)
                } else {
                    this.cancel()
                }
                return true
            }

            case 'Escape':
                this.cancel()
                return true

            case 'Backspace': {
                if (this._filter.length > 0) {
                    this._filter = this._filter.slice(0, -1)
                    this._updateItems()
                } else {
                    // Leerer Filter: Picker schliessen, @ bleibt stehen
                    this.close()
                }
                return true
            }

            case 'Tab':
                this.close()
                return true

            default:
                // Druckbares Zeichen → Filter erweitern
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                    this._filter += e.key
                    this._updateItems()
                    return true
                }
                // Modifier-Tasten und andere: ignorieren (aber Tasten verschlucken)
                return true
        }
    }

    /**
     * Verarbeitet Mouse-Move ueber dem Picker — Hover-Selection wandert mit
     * dem Mauszeiger ueber die Items, wie bei den anderen Menues.
     *
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn der Pointer ueber dem Picker liegt
     */
    handleMouseMove(charX, charY) {
        if (!this._active) return false
        // Picker aktiv, aber Bounds noch nicht gesetzt: signalisieren wir
        // "ueber dem Picker", damit der Cursor-Default bleibt und kein
        // Editor-Hover durchschlaegt. Selection wird beim naechsten Frame
        // (mit gesetzten Bounds) korrekt weitergeschaltet.
        if (!this._bounds) return true
        const b = this._bounds
        const inside = charX >= b.x && charX < b.x + b.width
            && charY >= b.y && charY < b.y + b.height
        if (!inside) return false

        // Offset = Top-Border + Eingabezeile + Trennlinie (siehe drawLinkPicker).
        const itemRow = charY - b.y - b.itemRowOffset
        if (itemRow < 0 || itemRow >= b.maxVisible) return true
        const itemIdx = b.scrollStart + itemRow
        // Defensiv gegen stale Bounds: zwischen Filter-Update und Re-Render
        // koennen _items kuerzer geworden sein als die Bounds noch annehmen.
        if (itemIdx < this._items.length && this._items[itemIdx]
            && itemIdx !== this._index) {
            this._index = itemIdx
            // Picker rendert nicht selbst — Main-Render-Loop holt sich den
            // neuen Index via getState() beim naechsten Frame.
            this.view.markDirty()
        }
        return true
    }

    /**
     * Verarbeitet einen Mausklick auf den Picker.
     * @param {number} charX - Klick-Position in Screen-Spalten
     * @param {number} charY - Klick-Position in Screen-Zeilen
     * @returns {boolean} true wenn der Klick vom Picker konsumiert wurde
     */
    handleClick(charX, charY) {
        if (!this._active) return false
        // Picker aktiv, aber Bounds noch nicht gesetzt (Race direkt nach
        // open() bevor das naechste Frame setBounds aufgerufen hat) — Klick
        // konsumieren statt durchlassen, sonst rutscht er an den Editor
        // und positioniert den Cursor "hinter" dem Picker.
        if (!this._bounds) return true
        const b = this._bounds

        // Ausserhalb → Picker schliessen, Klick weitergeben (Cursor setzen)
        if (charX < b.x || charX >= b.x + b.width || charY < b.y || charY >= b.y + b.height) {
            this.cancel()
            return false
        }

        // Klick auf ein Item → akzeptieren. Defensiv gegen stale Bounds:
        // zwischen Filter-Update und Re-Render koennen _items kuerzer
        // geworden sein als die alten Bounds noch annehmen — der Eintrag
        // an itemIdx kann dann fehlen.
        // Offset = Top-Border + Eingabezeile + Trennlinie (siehe drawLinkPicker).
        const itemRow = charY - b.y - b.itemRowOffset
        if (itemRow >= 0 && itemRow < b.maxVisible) {
            const itemIdx = b.scrollStart + itemRow
            const item = this._items[itemIdx]
            if (item) {
                this._accept(item.id)
                return true
            }
            // Stale-Bounds-Klick auf leere Region: ignorieren statt crashen.
        }

        return true  // Klick im Rahmen konsumieren
    }

    /**
     * Verarbeitet ein Mausrad-Event ueber dem Picker — zeilenweiser Scroll
     * durch die Item-Liste. Der selektierte Index bleibt unveraendert (er darf
     * dabei aus dem Sichtbereich rutschen, wie in anderen Listen-UIs).
     *
     * `deltaLines` darf fraktional sein (gedrosselte Sensitivity): die
     * Teil-Zeilen werden in `_wheelAccumulator` gesammelt, gescrollt wird erst
     * bei einer vollen Zeile.
     *
     * @param {number} deltaLines - Zeilen-Delta (negativ = hoch, positiv = runter)
     * @returns {boolean} true wenn sich der Scroll-Offset geaendert hat
     */
    handleWheel(deltaLines) {
        if (!this._active) return false
        this._wheelAccumulator += deltaLines
        const step = Math.trunc(this._wheelAccumulator)
        if (step === 0) return false
        this._wheelAccumulator -= step
        const maxScroll = Math.max(0, this._items.length - MAX_VISIBLE)
        const next = Math.max(0, Math.min(this._scrollOffset + step, maxScroll))
        if (next === this._scrollOffset) return false
        this._scrollOffset = next
        this.view.markDirty()
        return true
    }

    /**
     * Gibt den Picker-State fuer das Overlay-Rendering zurueck.
     * @returns {{active: boolean, items: Array, selectedIndex: number, filter: string, scrollOffset: number}|null}
     */
    getState() {
        if (!this._active) return null
        return {
            active: true,
            items: this._items,
            selectedIndex: this._index,
            filter: this._filter,
            scrollOffset: this._scrollOffset
        }
    }

    // ===== Private =====

    /**
     * Haelt den selektierten Index im Sichtbereich, indem der Scroll-Offset
     * mitwandert (nach Keyboard-Navigation). Mausrad-Scroll umgeht das bewusst.
     */
    _ensureIndexVisible() {
        const maxVisible = Math.min(this._items.length, MAX_VISIBLE)
        if (this._index < this._scrollOffset) {
            this._scrollOffset = this._index
        } else if (this._index >= this._scrollOffset + maxVisible) {
            this._scrollOffset = this._index - maxVisible + 1
        }
    }

    /** Aktualisiert die gefilterten Items basierend auf _filter. */
    _updateItems() {
        const items = []
        for (const [id, info] of this._sepMap) {
            // Display: Titel hat Vorrang, sonst die Nummer (ohne trailing Punkt)
            // — identisch zu model.resolveLinkLabel. Hat ein Separator ein Label,
            // wird die Kapitelnummer weggelassen: sie steht auch nicht im Link.
            const display = info.title || info.number.replace(/\.$/, '')
            items.push({ id, number: info.number, title: info.title, display })
        }

        const filter = this._filter.toLowerCase()
        this._items = filter
            ? items.filter(item => item.display.toLowerCase().includes(filter))
            : items

        // Index clampen
        if (this._index >= this._items.length) {
            this._index = Math.max(0, this._items.length - 1)
        }
        // Scroll-Offset an die neue Item-Zahl anpassen und den selektierten
        // Index wieder in den Sichtbereich holen (Filter kann die Liste stark
        // verkuerzen).
        const maxScroll = Math.max(0, this._items.length - MAX_VISIBLE)
        this._scrollOffset = Math.min(this._scrollOffset, maxScroll)
        this._ensureIndexVisible()
    }

    /**
     * Akzeptiert eine Auswahl: @ und Filter werden entfernt,
     * ein Link-Marker wird eingefuegt.
     * @param {string} separatorId
     */
    _accept(separatorId) {
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) { this.close(); return }

        const field = this.model.cursor.field || 'text'
        const text = this.model._getFieldText(para, field)

        // @ entfernen (Filter-Zeichen sind nur im Picker, nicht im Text) —
        // aber nur, wenn der Picker durch ein getipptes '@' geoeffnet wurde.
        // Wenn er programmatisch geoeffnet wurde (z.B. via Context Menu),
        // ist kein '@' im Text und es darf nichts entfernt werden.
        const removeLen = this._triggerCharPresent ? 1 : 0
        const removeStart = this.model.cursor.offset - removeLen
        if (removeStart < 0) { this.close(); return }

        // Gesamter Vorgang (Trigger entfernen + Link + Smart Blanks) soll EIN
        // Undo-Schritt sein — Snapshot vor allem, am Ende zusammenfassen.
        const hasInput = !!this.input
        const undoSize = this.model.undoStack.length
        const snapshot = hasInput ? this.input._takeSnapshot() : null

        const oldText = text
        const oldOffset = this.model.cursor.offset

        // Text modifizieren: @ entfernen (programmatisch = removeLen 0, No-Op)
        const newText = text.slice(0, removeStart) + text.slice(this.model.cursor.offset)
        this.model._setFieldText(para, field, newText)
        this.model.cursor.offset = removeStart
        this.model._markParagraphDirty(para.id)

        // Undo fuer das Entfernen des @-Triggers (vom Collapse spaeter ersetzt)
        const undoFn = () => {
            this.model._setFieldText(para, field, oldText)
            this.model.cursor = new Position(para.id, oldOffset, field)
            this.model._markParagraphDirty(para.id)
        }
        this.model._pushUndo(undoFn)
        this.model.charsSinceUndoPoint = 0

        // Link einfuegen (erzeugt eigenen Undo-Punkt)
        const insertOffset = this.model.cursor.offset
        this.model.insertLink(separatorId)

        // Smart Blanks: Leerzeichen vor/hinter dem Link analog zur Wort-Einfuegung
        // (Satzzeichen werden beruecksichtigt; der U+FFFC-Marker zaehlt als Wort).
        // Danach den gesamten Vorgang zu einem Undo-Schritt zusammenfassen.
        if (hasInput) {
            this.input._ensureSmartBlanks(insertOffset)
            this.input._collapseUndoEntries(undoSize, snapshot)
        }

        this.close()
        this.view.ensureCursorVisible()
    }
}
